import { createContext, useContext, useState } from "react";
import { useEffect } from "react";
import { auth, db } from "../firebase";

import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collectionGroup,
  getDocs,
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";

const FIELD_PATTERNS_PARTNO = [/part.?no/i, /part.?number/i, /model.?no/i];
const FIELD_PATTERNS_PRICE = [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i];
const FIELD_PATTERNS_NAME = [/^name$/i, /^title$/i, /^product.?name/i];
const FIELD_PATTERNS_IMAGE = [/^image$/i, /^photo$/i, /^picture$/i];
const FIELD_PATTERNS_SIZE = [/display.?size/i, /^size$/i, /^length$/i];

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));
const matchField = (fieldsObj, patterns) => {
  const entry = Object.entries(fieldsObj || {}).find(([key]) => testKey(key, patterns));
  return entry ? entry[1] ?? "" : "";
};

// Fetches every productData row and builds a map with FULL product details
const buildProductMap = async () => {
  const map = {};
  try {
    const snap = await getDocs(collectionGroup(db, "productData"));
    snap.forEach((rowDoc) => {
      const fields = rowDoc.data().fields || {};
      const partNo = matchField(fields, FIELD_PATTERNS_PARTNO) || rowDoc.id;
      
      // Get price with FULL decimal precision - store as number, not string
      const priceRaw = matchField(fields, FIELD_PATTERNS_PRICE);
      const price = parseFloat(priceRaw);
      
      // Also get other product details
      const name = matchField(fields, FIELD_PATTERNS_NAME);
      const image = matchField(fields, FIELD_PATTERNS_IMAGE);
      const displaySize = matchField(fields, FIELD_PATTERNS_SIZE);

      if (partNo && !isNaN(price)) {
        map[partNo] = {
          partNo,
          price: price,  // ← Store as precise number
          name: name,
          image: image,
          displaySize: displaySize,
        };
      }
    });
  } catch (error) {
    console.log("Product map build failed:", error);
  }
  return map;
};

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {

      if (!user) {
        setCartItems([]);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) return;

        const cartMap = userSnap.data().Cart || {};

        const cartArray = Object.entries(cartMap).map(
          ([partNo, quantity]) => ({
            partNo,
            quantity: Number(quantity)
          })
        );

        setCartItems(cartArray);

        if (cartArray.length > 0) {
          const productMap = await buildProductMap();

          // Merge cart with full product details
          const withDetails = cartArray.map((item) => ({
            partNo: item.partNo,
            quantity: item.quantity,
            price: productMap[item.partNo]?.price,  // ← Full precision number
            name: productMap[item.partNo]?.name,
            title: productMap[item.partNo]?.name,
            image: productMap[item.partNo]?.image,
            displaySize: productMap[item.partNo]?.displaySize,
          }));

          setCartItems(withDetails);
        }

      } catch (error) {
        console.log(error);
      }
    });

    return unsubscribe;
  }, []);

  const addToCart = async (product) => {

    const user = auth.currentUser;

    if (!user) {
      alert("Please login first");
      return;
    }

    // Ensure price is stored as precise number
    const normalizedProduct = {
      partNo: product.partNo,
      name: product.name || product.productName || product.title,
      title: product.title || product.name || product.productName,
      displaySize: product.displaySize,
      resolution: product.resolution,
      iface: product.iface,
      image: product.image,
      price: typeof product.price === 'string' 
        ? parseFloat(product.price)  // Convert string to number
        : Number(product.price),      // Ensure it's a number
      quantity: product.quantity || 1,
    };

    setCartItems((prev) => {

      const existingItem = prev.find(
        (item) => item.partNo === normalizedProduct.partNo
      );

      let updatedCart;

      if (existingItem) {
        updatedCart = prev.map((item) =>
          item.partNo === normalizedProduct.partNo
            ? {
              ...item,
              price: normalizedProduct.price,  // ← Use precise price
              quantity: (item.quantity || 1) + normalizedProduct.quantity
            }
            : item
        );
      } else {
        updatedCart = [...prev, normalizedProduct];
      }

      return updatedCart;
    });

    try {

      const userRef = doc(db, "users", user.uid);

      const userSnap = await getDoc(userRef);

      let existingCart = {};

      if (userSnap.exists()) {
        existingCart = userSnap.data().Cart || {};
      }

      existingCart[normalizedProduct.partNo] = String(
        (Number(existingCart[normalizedProduct.partNo]) || 0) +
        normalizedProduct.quantity
      );

      await updateDoc(userRef, {
        Cart: existingCart
      });

    } catch (error) {
      console.log(error);
    }
  };

  const removeFromCart = async (partNo) => {

    const user = auth.currentUser;

    setCartItems((prev) =>
      prev.filter((item) => item.partNo !== partNo)
    );

    if (!user) return;

    try {

      const userRef = doc(db, "users", user.uid);

      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const existingCart = userSnap.data().Cart || {};

      delete existingCart[partNo];

      await updateDoc(userRef, {
        Cart: existingCart
      });

    } catch (error) {
      console.log(error);
    }
  };


  const removeMultipleFromCart = async (partNos) => {

    const user = auth.currentUser;
    const partNoSet = new Set(partNos);

    setCartItems((prev) =>
      prev.filter((item) => !partNoSet.has(item.partNo))
    );

    if (!user) return;

    try {

      const userRef = doc(db, "users", user.uid);

      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const existingCart = userSnap.data().Cart || {};

      partNos.forEach((partNo) => delete existingCart[partNo]);

      await updateDoc(userRef, {
        Cart: existingCart
      });

    } catch (error) {
      console.log(error);
    }
  };

  const updateQuantity = async (partNo, newQty) => {

    if (newQty < 1) return;

    const user = auth.currentUser;

    setCartItems((prev) =>
      prev.map((item) =>
        item.partNo === partNo
          ? { ...item, quantity: newQty }
          : item
      )
    );

    if (!user) return;

    try {

      const userRef = doc(db, "users", user.uid);

      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const existingCart = userSnap.data().Cart || {};

      existingCart[partNo] = String(newQty);

      await updateDoc(userRef, {
        Cart: existingCart
      });

    } catch (error) {
      console.log(error);
    }
  };

  const clearCart = async () => {

    setCartItems([]);

    const user = auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        Cart: {}
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        removeMultipleFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);